import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Divider, Switch, Checkbox, Tooltip } from 'antd';
import { ZorbaButton, ZorbaSelect } from '../../../../components/forms';
import {
  BUTTON_VARIANT_DARK,
  BUTTON_VARIANT_LINK,
} from '../../../../components/forms/ZorbaButton/constants';
import { setModalState } from '../../../../reducers/Modal/actions';
import { MODAL_EDIT_FIELD } from '../../../../reducers/Modal/types';
import { updateBuyerDetails } from '../../../../reducers/Buyer/actions';
import { EditFieldModal } from '../EditFieldModal';
import { Webhooks } from '../Webhooks';
import { getNotificationsFieldsToEdit, formatPhoneNumber } from './constants';
import { showErrorToast } from '../../../../services/notificationsService';
import { isValidHttpUrl } from '../../../../services/urlService';
import { Spinner } from '../../../../components/Spinner';

export const Notifications = () => {
  const dispatch = useDispatch();
  const { organization, isSuperUser, userDetails } = useSelector(
    ({ AuthReducer }) => AuthReducer,
  );
  const { isLoading } = useSelector(({ BuyerReducer }) => BuyerReducer);
  const isMarketplaceUser = isSuperUser || organization?.marketplacePartner;
  const [selectedField, setSelectedField] = useState();
  const formattedNumber = formatPhoneNumber(userDetails?.phoneNumber?.number);

  const {
    unsubscribeFromNewDealsEmail,
    unsubscribeFromNewDealsSMS,
    unsubscribeFromEodEmail,
    unsubscribeFromEodSMS,
    unsubscribeFromOfferFollowUps,
    unsubscribeFromOfferFollowUpsSMS,
    webhooks,
  } = organization.buyerConfig;
  const { globalNotificationsRecipients } = organization;

  const fields = getNotificationsFieldsToEdit({
    unsubscribeFromNewDealsEmail,
    unsubscribeFromNewDealsSMS,
    unsubscribeFromEodEmail,
    unsubscribeFromEodSMS,
    unsubscribeFromOfferFollowUps,
    unsubscribeFromOfferFollowUpsSMS,
    globalNotificationsRecipients,
    webhooks,
  });

  const handleEdit = (field) => {
    setSelectedField(field);
    if (
      field.fieldName === 'newDeals' ||
      field.fieldName === 'dealFollowUps' ||
      field.fieldName === 'eodReport'
    ) {
      dispatch(setModalState(MODAL_EDIT_FIELD, true));
    }
  };

  const handleModalClose = () => {
    setSelectedField();
    dispatch(setModalState(MODAL_EDIT_FIELD, false));
  };

  const handleEmailSwitchChange = (checked) => {
    const fieldToConfigMap = {
      newDeals: 'unsubscribeFromNewDealsEmail',
      eodReport: 'unsubscribeFromEodEmail',
      dealFollowUps: 'unsubscribeFromOfferFollowUps',
    };

    const configField = fieldToConfigMap[selectedField.fieldName];
    if (!configField) return;

    setSelectedField({
      ...selectedField,
      emailValue: checked,
    });

    dispatch(
      updateBuyerDetails(
        {
          buyerConfig: {
            ...organization.buyerConfig,
            [configField]: !checked,
          },
        },
        'Notification settings updated successfully!',
        'Failed to update notification settings.',
      ),
    );
  };

  const handleSMSSwitchChange = (checked) => {
    const fieldToConfigMap = {
      newDeals: 'unsubscribeFromNewDealsSMS',
      eodReport: 'unsubscribeFromEodSMS',
      dealFollowUps: 'unsubscribeFromOfferFollowUpsSMS',
    };

    const configField = fieldToConfigMap[selectedField.fieldName];
    if (!configField) return;

    setSelectedField({
      ...selectedField,
      smsValue: checked,
    });
    dispatch(
      updateBuyerDetails(
        {
          buyerConfig: {
            ...organization.buyerConfig,
            [configField]: !checked,
          },
        },
        'Notification settings updated successfully!',
        'Failed to update notification settings.',
      ),
    );
  };

  const notificationsRecipientsOptions = organization.members.map((member) => ({
    text: member.email,
    value: member.email,
  }));

  const handleCancel = () => {
    setSelectedField(null);
  };

  const handleSaveGlobalNotificationRecipients = () => {
    dispatch(
      updateBuyerDetails(
        {
          globalNotificationsRecipients: selectedField.value,
        },
        'Notification settings updated successfully!',
        'Failed to update notification settings.',
        () => handleCancel(),
      ),
    );
  };

  const setWebhooks = (updatedWebhooks) => {
    setSelectedField({
      ...selectedField,
      value: updatedWebhooks,
    });
  };

  const handleSaveWebhooks = () => {
    const isValid =
      selectedField.value.filter((webhook) => {
        const hasNoEvents = !webhook.events?.length;
        const urlIsInvalid = !isValidHttpUrl(webhook.url);
        return hasNoEvents || urlIsInvalid;
      }).length > 0;

    if (isValid) {
      showErrorToast(
        'Please check your settings; they appear to be invalid. Correct them and submit again.',
      );
    } else {
      dispatch(
        updateBuyerDetails(
          {
            buyerConfig: {
              ...organization.buyerConfig,
              webhooks: selectedField.value,
            },
          },
          'Notification settings updated successfully!',
          'Failed to update notification settings.',
        ),
      );
    }
  };

  const isAnyUnsubscribed =
    !unsubscribeFromNewDealsEmail ||
    !unsubscribeFromNewDealsSMS ||
    !unsubscribeFromEodEmail ||
    !unsubscribeFromEodSMS ||
    !unsubscribeFromOfferFollowUps ||
    !unsubscribeFromOfferFollowUpsSMS;

  const handleCheckboxChange = (checked) => {
    if (checked) {
      dispatch(
        updateBuyerDetails(
          {
            buyerConfig: {
              ...organization.buyerConfig,
              unsubscribeFromNewDealsEmail: true,
              unsubscribeFromNewDealsSMS: true,
              unsubscribeFromEodEmail: true,
              unsubscribeFromEodSMS: true,
              unsubscribeFromOfferFollowUps: true,
              unsubscribeFromOfferFollowUpsSMS: true,
            },
          },
          'Notification settings updated successfully!',
          'Failed to update notification settings.',
        ),
      );
    } else {
      dispatch(
        updateBuyerDetails(
          {
            buyerConfig: {
              ...organization.buyerConfig,
              unsubscribeFromNewDealsEmail: false,
              unsubscribeFromNewDealsSMS: false,
              unsubscribeFromEodEmail: false,
              unsubscribeFromEodSMS: false,
              unsubscribeFromOfferFollowUps: false,
              unsubscribeFromOfferFollowUpsSMS: true,
            },
          },
          'Notification settings updated successfully!',
          'Failed to update notification settings.',
        ),
      );
    }
  };

  return (
    <>
      <div>
        <Tooltip title="Contact support@getzorba.com or your account manager to get API access to Zorba's inventory">
          <span>
            <ZorbaButton
              variant={BUTTON_VARIANT_DARK}
              disabled
              fullWidth={false}
            >
              Generate API Key
            </ZorbaButton>
          </span>
        </Tooltip>
        <Divider />
      </div>
      {fields.map((field) => (
        <>
          <div key={field.fieldName} className="field-container">
            <div className="description-container">
              <span className="label">{field.label}</span>
              {!(
                selectedField?.fieldName === field.fieldName &&
                field.fieldName === 'globalNotificationRecipients'
              ) && <span className="sub-title">{field.subTitle}</span>}
              {field?.textValue && (
                <span className="textValue">{field.textValue}</span>
              )}
              <div>
                {!(
                  selectedField?.fieldName === field.fieldName &&
                  (field.fieldName === 'globalNotificationRecipients' ||
                    field.fieldName === 'crmApiIntegration')
                ) && (
                  <ZorbaButton
                    variant={BUTTON_VARIANT_LINK}
                    fullWidth={false}
                    className="button notification"
                    onClick={() => handleEdit(field)}
                  >
                    Edit
                  </ZorbaButton>
                )}
              </div>
            </div>
          </div>
          {selectedField?.fieldName === field.fieldName &&
            selectedField.fieldName === 'globalNotificationRecipients' && (
              <div className="form-container">
                <div className="select-container">
                  <ZorbaSelect
                    mode="tags"
                    onChange={(e) =>
                      setSelectedField({
                        ...selectedField,
                        value: e,
                      })
                    }
                    value={selectedField.value}
                    options={notificationsRecipientsOptions}
                  />
                  <span className="global-notifications-hint">
                    The email addresses you enter here will receive every single
                    deal, in addition to the members of your company or the
                    recipients specified in the buy box recipients feature.
                  </span>
                </div>
                <div className="buttons-container">
                  <ZorbaButton
                    fullWidth={false}
                    variant={BUTTON_VARIANT_DARK}
                    onClick={handleSaveGlobalNotificationRecipients}
                    disabled={isLoading}
                    loading={isLoading}
                  >
                    Save
                  </ZorbaButton>
                  <ZorbaButton
                    fullWidth={false}
                    variant={BUTTON_VARIANT_LINK}
                    onClick={handleCancel}
                    disabled={isLoading}
                  >
                    Cancel
                  </ZorbaButton>
                </div>
              </div>
            )}
          {selectedField?.fieldName === field.fieldName &&
            selectedField.fieldName === 'crmApiIntegration' && (
              <Webhooks
                webhooks={selectedField.value}
                setWebhooks={setWebhooks}
                handleCancel={handleCancel}
                handleSave={handleSaveWebhooks}
                isLoading={isLoading}
              />
            )}
          <Divider className="divider" />
        </>
      ))}
      <div className="unsubscribe-checkbox-container">
        <Checkbox
          checked={!isAnyUnsubscribed}
          disabled={isLoading}
          loading={isLoading}
          onChange={(event) => handleCheckboxChange(event.target.checked)}
        >
          Unsubscribe from all marketing emails and SMS
        </Checkbox>
        {isLoading ? (
          <Spinner />
        ) : (
          <span className="checkbox-description">
            {`By opting in to text messages, you agree to receive automated
          marketing messages from Zorba at +1 ${formattedNumber}. To receive messages
          at a different number, update your phone number settings in the
          account tab under the settings page.`}
          </span>
        )}
      </div>
      <EditFieldModal
        selectedField={selectedField}
        setSelectedField={setSelectedField}
      >
        <div className="notifications-content-container">
          <span className="sub-title">{selectedField?.subTitle}</span>
          <div className="switch-notification-container">
            <span className="switch-label">Email</span>
            <Switch
              checked={selectedField?.emailValue}
              onChange={handleEmailSwitchChange}
            />
          </div>
          {isMarketplaceUser && (
            <div className="switch-notification-container">
              <span className="switch-label">SMS</span>
              <Switch
                checked={selectedField?.smsValue}
                onChange={handleSMSSwitchChange}
              />
            </div>
          )}
          <div className="notification-button-container">
            <ZorbaButton
              fullWidth={false}
              variant={BUTTON_VARIANT_DARK}
              onClick={handleModalClose}
              disabled={isLoading}
              loading={isLoading}
            >
              Close
            </ZorbaButton>
          </div>
        </div>
      </EditFieldModal>
    </>
  );
};
